import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newHoliday, setNewHoliday] = useState({
    start_date: '',
    end_date: '',
    holiday_name: '',
    holiday_type: 'national'
  });

  useEffect(() => {
    loadHolidays();
  }, [selectedYear]);

  const loadHolidays = async () => {
    setLoading(true);
    try {
      // ⚠️ Backend จะส่งกลับมาเฉพาะ "วันหยุด" ของโรงเรียนที่แอดมินสังกัดอยู่เท่านั้น
      const data = await api.getHolidays(selectedYear);
      setHolidays(data);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.start_date || !newHoliday.holiday_name) {
      alert('กรุณากรอกวันที่เริ่มต้นและชื่อวันหยุด');
      return;
    }

    const endDate = newHoliday.end_date || newHoliday.start_date;

    if (new Date(endDate) < new Date(newHoliday.start_date)) {
      alert('วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น');
      return;
    }

    setSubmitting(true);
    try {
      // ⚠️ Backend จะผูก organization_id ของแอดมิน เข้ากับวันหยุดนี้ให้อัตโนมัติ
      await api.addHoliday({
        start_date: newHoliday.start_date,
        end_date: endDate,
        holiday_name: newHoliday.holiday_name,
        holiday_type: newHoliday.holiday_type
      });
      
      setNewHoliday({
        start_date: '',
        end_date: '',
        holiday_name: '',
        holiday_type: 'national'
      });
      setShowAddModal(false);
      loadHolidays();
      alert('เพิ่มวันหยุดเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error adding holiday:', error);
      alert('เกิดข้อผิดพลาดในการเพิ่มวันหยุด: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteHoliday = async (id, name) => {
    if (window.confirm(`คุณต้องการลบวันหยุด "${name}" ใช่หรือไม่?`)) {
      try {
        // ⚠️ Backend จะป้องกันความปลอดภัย อนุญาตให้ลบได้เฉพาะวันหยุดของโรงเรียนตัวเอง
        await api.deleteHoliday(id);
        loadHolidays();
      } catch (error) {
        console.error('Error deleting holiday:', error);
        alert('เกิดข้อผิดพลาดในการลบวันหยุด: ' + error.message);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getHolidayTypeLabel = (type) => {
    switch (type) {
      case 'national': return 'วันหยุดราชการ';
      case 'company': return 'วันหยุดพิเศษ';
      default: return 'อื่นๆ';
    }
  };

  const getHolidayTypeColor = (type) => {
    switch (type) {
      case 'national': return 'bg-red-100 text-red-800';
      case 'company': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i);

  if (loading && holidays.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-8 w-8 text-blue-600" />
            จัดการวันหยุด
          </h1>
          <p className="text-gray-500 mt-1">กำหนดวันหยุดประจำปีของโรงเรียน (ระบบจะไม่นับเป็นวันขาดงาน)</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 transition-colors shadow-sm"
        >
          <Plus className="h-5 w-5" />
          <span>เพิ่มวันหยุด</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-800">รายการวันหยุด</h2>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-600">ปีปฏิทิน:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {years.map(year => (
                <option key={year} value={year}>{year + 543}</option>
              ))}
            </select>
          </div>
        </div>
        
        {holidays.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">ไม่พบข้อมูลวันหยุด</h3>
            <p className="text-gray-500">ในปี พ.ศ. {selectedYear + 543} ยังไม่มีการกำหนดวันหยุด</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm border-b border-gray-100">
                  <th className="px-6 py-4 font-semibold">วันที่</th>
                  <th className="px-6 py-4 font-semibold">ชื่อวันหยุด</th>
                  <th className="px-6 py-4 font-semibold">ประเภท</th>
                  <th className="px-6 py-4 font-semibold text-center w-24">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {holidays.map((holiday) => (
                  <tr key={holiday.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatDate(holiday.holiday_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      {holiday.holiday_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getHolidayTypeColor(holiday.holiday_type)}`}>
                        {getHolidayTypeLabel(holiday.holiday_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteHoliday(holiday.id, holiday.holiday_name)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        title="ลบวันหยุด"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">เพิ่มวันหยุด</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex gap-3 text-sm text-blue-800 mb-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 text-blue-600" />
                <p>การเพิ่มวันหยุดจะทำให้ระบบไม่นับการขาดงานในวันนั้น (ยกเว้นผู้ที่มีลงเวลาพิเศษ)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ตั้งแต่วันที่ *</label>
                  <input
                    type="date"
                    value={newHoliday.start_date}
                    onChange={(e) => setNewHoliday({...newHoliday, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่ (ไม่บังคับ)</label>
                  <input
                    type="date"
                    value={newHoliday.end_date}
                    onChange={(e) => setNewHoliday({...newHoliday, end_date: e.target.value})}
                    min={newHoliday.start_date}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">ปล่อยว่างหากหยุดแค่วันเดียว</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อวันหยุด *</label>
                <input
                  type="text"
                  value={newHoliday.holiday_name}
                  onChange={(e) => setNewHoliday({...newHoliday, holiday_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น วันสงกรานต์"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ประเภท</label>
                <select
                  value={newHoliday.holiday_type}
                  onChange={(e) => setNewHoliday({...newHoliday, holiday_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="national">วันหยุดราชการ</option>
                  <option value="company">วันหยุดพิเศษ</option>
                  <option value="other">อื่นๆ</option>
                </select>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex gap-3 justify-end">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleAddHoliday}
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium shadow-sm hover:shadow transition-all disabled:opacity-70 flex items-center gap-2"
              >
                {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                <Save className="h-4 w-4" />
                บันทึก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayManagement;